import type { Place } from '@elara/client-core/models/PlaceModel';

import type { IconName } from '#ui/MaterialIcon.js';

import { Card, CardContent, CardHeader, CardTitle } from '#ui/card.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

interface AmenityItem {
  key: string;
  label: string;
  icon: IconName;
}

interface AmenityGroup {
  title: string;
  icon: IconName;
  items: AmenityItem[];
}

const AMENITY_GROUPS: AmenityGroup[] = [
  {
    title: 'Services',
    icon: 'room_service',
    items: [
      { key: 'dineIn', label: 'Dine-in', icon: 'dinner_dining' },
      { key: 'takeout', label: 'Takeout', icon: 'takeout_dining' },
      { key: 'delivery', label: 'Delivery', icon: 'local_shipping' },
      { key: 'curbsidePickup', label: 'Curbside pickup', icon: 'storefront' },
      { key: 'reservable', label: 'Reservable', icon: 'event_seat' },
    ],
  },
  {
    title: 'Dining options',
    icon: 'menu_book',
    items: [
      { key: 'servesBreakfast', label: 'Breakfast', icon: 'breakfast_dining' },
      { key: 'servesBrunch', label: 'Brunch', icon: 'brunch_dining' },
      { key: 'servesLunch', label: 'Lunch', icon: 'lunch_dining' },
      { key: 'servesDinner', label: 'Dinner', icon: 'dinner_dining' },
      { key: 'servesCoffee', label: 'Coffee', icon: 'coffee' },
      { key: 'servesCocktails', label: 'Cocktails', icon: 'local_bar' },
      { key: 'servesBeer', label: 'Beer', icon: 'sports_bar' },
      { key: 'servesWine', label: 'Wine', icon: 'wine_bar' },
      { key: 'servesDessert', label: 'Dessert', icon: 'cake' },
      { key: 'servesVegetarianFood', label: 'Vegetarian food', icon: 'eco' },
      { key: 'menuForChildren', label: 'Children’s menu', icon: 'child_care' },
    ],
  },
  {
    title: 'Amenities',
    icon: 'local_activity',
    items: [
      { key: 'outdoorSeating', label: 'Outdoor seating', icon: 'deck' },
      { key: 'liveMusic', label: 'Live music', icon: 'music_note' },
      { key: 'restroom', label: 'Restroom', icon: 'wc' },
      { key: 'goodForChildren', label: 'Good for children', icon: 'child_care' },
      { key: 'goodForGroups', label: 'Good for groups', icon: 'groups' },
      { key: 'goodForWatchingSports', label: 'Good for watching sports', icon: 'live_tv' },
      { key: 'allowsDogs', label: 'Dogs allowed', icon: 'pets' },
    ],
  },
  {
    title: 'Accessibility',
    icon: 'accessible',
    items: [
      { key: 'accessibilityOptions.wheelchairAccessibleParking', label: 'Accessible parking', icon: 'local_parking' },
      { key: 'accessibilityOptions.wheelchairAccessibleEntrance', label: 'Accessible entrance', icon: 'accessible' },
      { key: 'accessibilityOptions.wheelchairAccessibleRestroom', label: 'Accessible restroom', icon: 'wc' },
      { key: 'accessibilityOptions.wheelchairAccessibleSeating', label: 'Accessible seating', icon: 'event_seat' },
    ],
  },
  // {
  //   title: 'Parking',
  //   icon: 'local_parking',
  //   items: [
  //     { key: 'parkingOptions.freeParkingLot', label: 'Free parking lot', icon: 'local_parking' },
  //     { key: 'parkingOptions.paidParkingLot', label: 'Paid parking lot', icon: 'local_parking' },
  //     { key: 'parkingOptions.freeStreetParking', label: 'Free street parking', icon: 'directions_car' },
  //     { key: 'parkingOptions.paidStreetParking', label: 'Paid street parking', icon: 'directions_car' },
  //     { key: 'parkingOptions.valetParking', label: 'Valet parking', icon: 'hail' },
  //     { key: 'parkingOptions.freeGarageParking', label: 'Free garage parking', icon: 'garage' },
  //     { key: 'parkingOptions.paidGarageParking', label: 'Paid garage parking', icon: 'garage' },
  //   ],
  // },
];

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

export function PlaceAmenities({ place }: { place: Place }) {
  const activeGroups = AMENITY_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => getNestedValue(place as unknown as Record<string, unknown>, item.key) === true),
  })).filter((group) => group.items.length > 0);

  if (activeGroups.length === 0) return null;

  return (
    <>
      <hr />
      <Card>
        <CardHeader className="sr-only">
          <CardTitle>Amenities & services</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 grid-cols-1 @md:grid-cols-2 @2xl:grid-cols-4">
            {activeGroups.map((group) => (
              <div key={group.title}>
                <div className="flex items-center gap-1.5 mb-2 text-sm font-medium text-muted-foreground">
                  <span>{group.title}</span>
                </div>
                <div className="flex flex-col gap-2">
                  {group.items.map((item) => (
                    <span key={item.key} className="inline-flex items-center gap-1.5">
                      <MaterialIcon icon={item.icon} size={IconSizes.sm} className="text-muted-foreground" />
                      {item.label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
}
